import React, { useEffect, useState } from 'react';
import { hämtaKunder, hämtaInkopare, Kund, Inkopare } from '../api/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TEMA, useMobil } from '../theme';
import SokbarLista from '../components/SokbarLista';

export default function AllaKunder() {
  const [kunder, setKunder] = useState<Kund[]>([]);
  const [inkopare, setInkopare] = useState<Inkopare[]>([]);
  const [searchParams] = useSearchParams();
  const [sökning, setSökning] = useState(searchParams.get('q') || '');
  const [typFilter, setTypFilter] = useState<'alla' | 'kund' | 'prospect'>('alla');
  const [stadFilter, setStadFilter] = useState('');
  const [säljarFilter, setSäljarFilter] = useState('');
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);
  const navigate = useNavigate();
  const ärMobil = useMobil();

  useEffect(() => {
    setLaddar(true);
    setFel(null);
    Promise.all([hämtaKunder(), hämtaInkopare()])
      .then(([k, i]) => {
        setKunder(k.data);
        setInkopare(i.data);
        setLaddar(false);
        requestAnimationFrame(() => {
          const sparadPosition = sessionStorage.getItem('alla-kunder-scroll');
          if (sparadPosition) {
            window.scrollTo(0, parseInt(sparadPosition, 10));
          }
        });
      })
      .catch((err) => {
        console.error('Kunde inte ladda kunder:', err);
        setFel('Kunde inte ladda kundlistan just nu. Försök igen om en stund.');
        setLaddar(false);
      });
  }, []);

  // Flera kunder kan dela bolagsnamn, t.ex. ett hotell och dess restaurang.
  // För dem visas Fortnox-numret, som är det enda som skiljer dem åt.
  const namnAntal = kunder.reduce<Record<string, number>>((acc, k) => {
    acc[k.namn] = (acc[k.namn] || 0) + 1;
    return acc;
  }, {});

  const aktiva = kunder.filter(k => k.aktiv);

  const städer = Array.from(new Set(
    aktiva.map(k => (k.stad || '').trim()).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, 'sv'));

  const säljare = Array.from(new Set(
    aktiva.map(k => (k.ansvarig_säljare || '').trim()).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, 'sv'));

  const harFilter = typFilter !== 'alla' || !!stadFilter || !!säljarFilter || !!sökning;

  // Allt utom valet Alla/Kunder/Prospects, så att knapparna kan visa hur många
  // som finns av varje slag med nuvarande sökning och filter.
  const utanTyp = aktiva
    .filter(k => !stadFilter || (k.stad || '').trim().toLowerCase() === stadFilter.toLowerCase())
    .filter(k => !säljarFilter || (k.ansvarig_säljare || '').trim() === säljarFilter)
    .filter(k => {
      const s = sökning.toLowerCase();
      const kundInkopare = inkopare.filter(i => i.kund_id === k.kund_id);
      return (
        (k.namn || '').toLowerCase().includes(s) ||
        (k.stad || '').toLowerCase().includes(s) ||
        (k.matkoncept || '').toLowerCase().includes(s) ||
        (k.ansvarig_säljare || '').toLowerCase().includes(s) ||
        (k.fortnox_kund_nr || '').toLowerCase().includes(s) ||
        kundInkopare.some(i => (i.namn || '').toLowerCase().includes(s))
      );
    });

  // Kund eller prospect avgörs av om kunden finns i Fortnox.
  const antal = {
    alla: utanTyp.length,
    kund: utanTyp.filter(k => !!k.fortnox_kund_nr).length,
    prospect: utanTyp.filter(k => !k.fortnox_kund_nr).length,
  };

  const filtrerade = utanTyp
    .filter(k => typFilter === 'alla'
      || (typFilter === 'kund' ? !!k.fortnox_kund_nr : !k.fortnox_kund_nr))
    .sort((a, b) => a.namn.localeCompare(b.namn));

  if (laddar) return <p style={{ color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Laddar...</p>;
  if (fel) return <p style={{ color: '#D9695F', fontFamily: "'Inter', sans-serif" }}>{fel}</p>;

  return (
    <div>
      <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink, margin: '0 0 20px' }}>Alla kunder</h1>

      <input
        type="text"
        placeholder={ärMobil ? "Sök kund, säljare, inköpare…" : "Sök på namn, stad, säljare, inköpare eller kundnummer..."}
        value={sökning}
        onChange={e => setSökning(e.target.value)}
        style={{
          width: '100%',
          padding: '10px 14px',
          borderRadius: '2px',
          border: `1px solid ${TEMA.border}`,
          background: TEMA.bgPanel,
          color: TEMA.ink,
          fontFamily: "'Inter', sans-serif",
          fontSize: '14px',
          outline: 'none',
          boxSizing: 'border-box',
          marginBottom: '14px',
        }}
      />

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', borderRadius: '2px', border: `1px solid ${TEMA.border}`, overflow: 'hidden' }}>
          {([['alla', 'Alla'], ['kund', 'Kunder'], ['prospect', 'Prospects']] as const).map(([värde, etikett]) => (
            <button
              key={värde}
              onClick={() => setTypFilter(värde)}
              style={{
                padding: '9px 16px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                fontFamily: "'Inter', sans-serif", fontSize: '13px',
                fontWeight: typFilter === värde ? 500 : 400,
                background: typFilter === värde ? TEMA.accent : 'transparent',
                color: typFilter === värde ? TEMA.onAccent : TEMA.inkSoft,
              }}
            >
              {etikett}
              <span style={{
                marginLeft: '7px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px',
                opacity: typFilter === värde ? 0.75 : 0.6,
              }}>
                {antal[värde]}
              </span>
            </button>
          ))}
        </div>

        <div style={ärMobil ? { flex: '1 1 140px', minWidth: 0 } : { width: '200px' }}>
          <SokbarLista
            värde={stadFilter}
            onChange={setStadFilter}
            placeholder="Alla städer"
            alternativ={städer.map(s => ({ värde: s, etikett: s }))}
          />
        </div>

        <div style={ärMobil ? { flex: '1 1 140px', minWidth: 0 } : { width: '190px' }}>
          <SokbarLista
            värde={säljarFilter}
            onChange={setSäljarFilter}
            placeholder="Alla säljare"
            alternativ={säljare.map(s => ({ värde: s, etikett: s }))}
          />
        </div>

        {harFilter && (
          <button
            onClick={() => { setTypFilter('alla'); setStadFilter(''); setSäljarFilter(''); setSökning(''); }}
            style={{
              background: 'none', border: 'none', padding: '0 4px', cursor: 'pointer',
              fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkFaint,
            }}
          >
            Rensa
          </button>
        )}
      </div>

      <button
        onClick={() => navigate('/registrera-kund')}
        style={{
          padding: '9px 18px',
          borderRadius: '2px',
          background: TEMA.accent,
          color: TEMA.onAccent,
          fontFamily: "'Inter', sans-serif",
          fontSize: '13px',
          fontWeight: 500,
          border: `1px solid ${TEMA.accent}`,
          cursor: 'pointer',
          marginBottom: '24px',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = TEMA.accentHover; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = TEMA.accent; }}
      >
        + Lägg till ny kund
      </button>

      {filtrerade.map(k => {
        const kundInkopare = inkopare.filter(i => i.kund_id === k.kund_id);
        const delatNamn = namnAntal[k.namn] > 1;
        return (
          <div
            key={k.kund_id}
            onClick={() => {
              sessionStorage.setItem('alla-kunder-scroll', String(window.scrollY));
              navigate(`/kund/${k.kund_id}`);
            }}
            style={{
              background: TEMA.bgPanel,
              border: `1px solid ${TEMA.border}`,
              borderRadius: '8px',
              padding: ärMobil ? '14px 16px' : '16px 20px',
              marginBottom: '10px',
              cursor: 'pointer',
              transition: 'border-color 0.15s ease',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = TEMA.inkSoft; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = TEMA.border; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 10px', marginBottom: '8px' }}>
              <span style={{ fontFamily: "'Fraunces', serif", fontSize: '16px', color: TEMA.ink, minWidth: 0, flex: ärMobil ? '1 1 100%' : undefined }}>{k.namn}</span>
              {delatNamn && k.fortnox_kund_nr && (
                <span
                  title="Flera kunder delar namn — det här är kundnumret i Fortnox"
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: '11px', letterSpacing: '0.04em', padding: '3px 10px',
                    borderRadius: '2px', border: `1px solid ${TEMA.accent}`, color: TEMA.accent,
                  }}
                >
                  #{k.fortnox_kund_nr}
                </span>
              )}
              {k.segment && (
                <span style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: '11px', letterSpacing: '0.04em', padding: '3px 10px',
                  borderRadius: '2px', border: `1px solid ${TEMA.border}`, color: TEMA.inkSoft,
                }}>
                  Segment {k.segment}
                </span>
              )}
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '11px', letterSpacing: '0.04em', padding: '3px 10px',
                borderRadius: '2px',
                border: `1px solid ${k.fortnox_kund_nr ? TEMA.positive : TEMA.border}`,
                color: k.fortnox_kund_nr ? TEMA.positive : TEMA.inkSoft,
              }}>
                {k.fortnox_kund_nr ? 'Kund' : 'Prospect'}
              </span>
              <span style={{ marginLeft: ärMobil ? 0 : 'auto', fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                {[k.stad, k.matkoncept].filter(v => (v || '').trim()).join(' · ')}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px 16px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", minWidth: 0 }}>
                {kundInkopare.length > 0
                  ? kundInkopare.map(i => `${i.namn} (${i.roll})`).join(' · ')
                  : 'Inga inköpare registrerade'}
              </p>
              <p style={{ margin: 0, fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                {k.ansvarig_säljare || 'Ej tilldelad'}
              </p>
            </div>
          </div>
        );
      })}

      {filtrerade.length === 0 && (
        <p style={{ color: TEMA.inkFaint, textAlign: 'center', marginTop: '48px', fontFamily: "'Inter', sans-serif" }}>
          Inga kunder matchade sökningen.
        </p>
      )}
    </div>
  );
}